import { sortTiles, type Meld, type Tile } from '../game/types.js';
import type { Rect, SeatZone } from '../layout/types.js';
import {
  DRAWN_TILE_LIFT_PX,
  HAND_TILE_GAP_PX,
  MELD_END_INSET_PX,
  MELD_TILE_GAP_PX,
  MELD_TILE_SCALE,
  MIN_TILE_STRIDE_PX,
  TILE_HEIGHT,
  TILE_WIDTH,
} from '../utils/constants.js';
import type { RenderPalette } from './palette.js';
import { drawTileBack, drawTileFace, type TileRotation } from './TileRenderer.js';
import { meldSlots, spreadRow } from './tileLayout.js';

/** Tiles laid along the horizontal edge (bottom/top) vs the vertical edge. */
function isHorizontal(rotation: TileRotation): boolean {
  return rotation === 0 || rotation === 180;
}

export interface LocalHandOptions {
  /** The just-drawn tile — lifted + glowing so the player sees what arrived. */
  readonly drawnTileId?: number | null;
  /** The tile under the player's selection ring (input state; unused pre-T8). */
  readonly selectedTileId?: number | null;
  /** Tile ids to ring with the claim-glow highlight (lessons "follow this tile"). */
  readonly highlightTileIds?: readonly number[];
}

/**
 * The human's face-up rack along the bottom zone: sorted, evenly spread, with
 * the drawn tile lifted and any selected tile ringed.
 */
export function drawLocalHand(
  ctx: CanvasRenderingContext2D,
  zone: SeatZone,
  hand: readonly Tile[],
  palette: RenderPalette,
  options: LocalHandOptions = {},
): void {
  const tiles = sortTiles(hand);
  const spread = spreadRow(tiles.length, TILE_WIDTH, HAND_TILE_GAP_PX, MIN_TILE_STRIDE_PX, zone.width);
  const cy = zone.y + zone.height / 2;
  for (let i = 0; i < spread.visibleCount; i += 1) {
    const tile = tiles[i];
    if (tile === undefined) continue;
    const cx = zone.x + spread.startCenter + i * spread.stride;
    const lifted = options.drawnTileId != null && tile.id === options.drawnTileId;
    const highlighted = options.highlightTileIds?.includes(tile.id) ?? false;
    const y = cy - TILE_HEIGHT / 2 - (lifted ? DRAWN_TILE_LIFT_PX : 0);
    drawTileFace(
      ctx,
      { x: cx - TILE_WIDTH / 2, y, width: TILE_WIDTH, height: TILE_HEIGHT, rotation: 0 },
      tile,
      palette,
      { selected: options.selectedTileId === tile.id, hover: lifted, highlight: highlighted },
    );
  }
}

/**
 * An opponent's concealed hand as edge-on tile backs, count-driven and
 * oriented toward table centre by the seat's rotation. Overlaps to fit like
 * domino's opponent stack.
 */
export function drawOpponentHand(
  ctx: CanvasRenderingContext2D,
  zone: SeatZone,
  tileCount: number,
  palette: RenderPalette,
  rotation: TileRotation,
): void {
  const horizontal = isHorizontal(rotation);
  const availableMajor = horizontal ? zone.width : zone.height;
  const spread = spreadRow(tileCount, TILE_WIDTH, HAND_TILE_GAP_PX, MIN_TILE_STRIDE_PX, availableMajor);
  const crossCenter = horizontal ? zone.y + zone.height / 2 : zone.x + zone.width / 2;
  for (let i = 0; i < spread.visibleCount; i += 1) {
    const major = (horizontal ? zone.x : zone.y) + spread.startCenter + i * spread.stride;
    const cx = horizontal ? major : crossCenter;
    const cy = horizontal ? crossCenter : major;
    drawTileBack(
      ctx,
      { x: cx - TILE_WIDTH / 2, y: cy - TILE_HEIGHT / 2, width: TILE_WIDTH, height: TILE_HEIGHT, rotation },
      palette,
    );
  }
}

/**
 * An opponent's concealed hand revealed FACE-UP at hand-end — same geometry as
 * `drawOpponentHand` but drawing sorted faces (rotated to the seat) instead of
 * backs, so the owner can study everyone's tiles once the hand is over.
 */
export function drawOpponentHandFaceUp(
  ctx: CanvasRenderingContext2D,
  zone: SeatZone,
  hand: readonly Tile[],
  palette: RenderPalette,
  rotation: TileRotation,
): void {
  const tiles = sortTiles(hand);
  const horizontal = isHorizontal(rotation);
  const availableMajor = horizontal ? zone.width : zone.height;
  const spread = spreadRow(tiles.length, TILE_WIDTH, HAND_TILE_GAP_PX, MIN_TILE_STRIDE_PX, availableMajor);
  const crossCenter = horizontal ? zone.y + zone.height / 2 : zone.x + zone.width / 2;
  for (let i = 0; i < spread.visibleCount; i += 1) {
    const tile = tiles[i];
    if (tile === undefined) continue;
    const major = (horizontal ? zone.x : zone.y) + spread.startCenter + i * spread.stride;
    const cx = horizontal ? major : crossCenter;
    const cy = horizontal ? crossCenter : major;
    drawTileFace(
      ctx,
      { x: cx - TILE_WIDTH / 2, y: cy - TILE_HEIGHT / 2, width: TILE_WIDTH, height: TILE_HEIGHT, rotation },
      tile,
      palette,
    );
  }
}

interface MeldTile {
  readonly tile: Tile;
  /** True only for the two middle copies of a self-drawn concealed kong —
   *  the two tiles never exposed to anyone stay face-down; the outer two
   *  stay visible so the meld's identity still reads at a glance. */
  readonly faceDown: boolean;
}

/** Concealed-kong middle tiles are indices 1 and 2 of that meld's own
 *  4-tile array — the conventional "hidden middle" of a laid-out row. All
 *  4 copies are physically identical, so which 2 get flagged carries no
 *  information either way. */
const CONCEALED_KONG_FACE_DOWN_INDICES: readonly number[] = [1, 2];

function meldTiles(melds: readonly Meld[]): MeldTile[] {
  return melds.flatMap((meld) => {
    const hideMiddle = meld.type === 'kong' && meld.concealed;
    return meld.tiles.map((tile, i) => ({
      tile,
      faceDown: hideMiddle && CONCEALED_KONG_FACE_DOWN_INDICES.includes(i),
    }));
  });
}

/**
 * A seat's open (called) melds, face-up and reduced, tucked against the
 * trailing end of `zone` — any rect works, not just a rack `SeatZone`; called
 * from the pond's meld strip (see `DiscardRenderer`/`tileLayout.splitPondCell`)
 * so a called set renders on the table under that seat's discards, not beside
 * their concealed hand. No-op when the seat has made no calls. A concealed
 * kong's two middle tiles render face-down (see `meldTiles`); every other
 * meld renders fully face-up.
 */
export function drawMelds(
  ctx: CanvasRenderingContext2D,
  zone: Rect,
  melds: readonly Meld[],
  palette: RenderPalette,
  rotation: TileRotation,
): void {
  const tiles = meldTiles(melds);
  if (tiles.length === 0) return;

  const w = TILE_WIDTH * MELD_TILE_SCALE;
  const h = TILE_HEIGHT * MELD_TILE_SCALE;
  const horizontal = isHorizontal(rotation);
  const slots = meldSlots(zone, tiles.length, w, MELD_TILE_GAP_PX, MELD_END_INSET_PX, horizontal);

  for (let i = 0; i < tiles.length; i += 1) {
    const slot = slots[i];
    const entry = tiles[i];
    if (slot === undefined || entry === undefined) continue;
    const rect = { x: slot.cx - w / 2, y: slot.cy - h / 2, width: w, height: h, rotation };
    if (entry.faceDown) {
      drawTileBack(ctx, rect, palette);
    } else {
      drawTileFace(ctx, rect, entry.tile, palette);
    }
  }
}
